(function () {
  const VARIANT_SELECTOR = '[data-mixo-preorder-variants]';
  const PROPERTY_SELECTOR = '[data-preorder-property]';
  const FLAG_SELECTOR = '[data-preorder-flag]';
  const NOTE_SELECTOR = '[data-preorder-note]';
  const ADD_TEXT_SELECTOR = '[data-add-to-cart-text]';
  const BUY_NOW_SELECTOR = '[data-preorder-buy-now]';

  const payloadCache = new WeakMap();
  let cartPromise = null;
  let cartData = null;

  function parsePayload(form) {
    if (payloadCache.has(form)) return payloadCache.get(form);

    const script = form.querySelector(VARIANT_SELECTOR);
    let payload = { productId: null, stockBottles: 0, cartBottles: 0, variants: [] };

    if (script) {
      try {
        const parsed = JSON.parse(script.textContent);
        if (Array.isArray(parsed)) {
          payload.variants = parsed;
        } else if (parsed && typeof parsed === 'object') {
          payload = {
            productId: parsed.productId,
            stockBottles: Number(parsed.stockBottles) || 0,
            cartBottles: Number(parsed.cartBottles) || 0,
            variants: Array.isArray(parsed.variants) ? parsed.variants : [],
          };
        }
      } catch (error) {
        payload = { productId: null, stockBottles: 0, cartBottles: 0, variants: [] };
      }
    }

    payloadCache.set(form, payload);
    return payload;
  }

  function parseBottleUnits(title) {
    const normalizedTitle = String(title || '').toLowerCase().trim();
    if (!normalizedTitle || normalizedTitle === 'default title') return 1;

    const packCountMatch = normalizedTitle.match(/^(\d+)/);
    const packCount = packCountMatch ? Number.parseInt(packCountMatch[1], 10) : 1;
    const ofMatch = normalizedTitle.match(/(?:of|de)\s+(\d+)/);

    if (ofMatch) {
      return Math.max(1, packCount) * Number.parseInt(ofMatch[1], 10);
    }

    if (/bottle|bouteille/.test(normalizedTitle)) {
      return Math.max(1, packCount);
    }

    return 1;
  }

  function variantUnits(variant) {
    if (!variant) return 1;
    const explicit = Number(variant.units);
    if (Number.isFinite(explicit) && explicit > 0) return explicit;
    return parseBottleUnits(variant.title);
  }

  function stockBottlesFromVariants(variants) {
    const list = Array.isArray(variants) ? variants : [];
    const single = list.find((variant) => /^1\s*(bottle|bouteille)$/i.test(String(variant.title || '').trim()));

    if (single && Number.isFinite(Number(single.quantity))) {
      return Math.max(0, Number(single.quantity));
    }

    const implied = list
      .map((variant) => Math.max(0, Number(variant.quantity) || 0) * variantUnits(variant))
      .filter((value) => value > 0);

    return implied.length ? Math.min(...implied) : 0;
  }

  function getQuantityInput(form) {
    if (!form) return null;

    const scope = form.closest('product-info, .product, .shopify-section') || document;

    if (form.id) {
      const linked = scope.querySelector(`input[name="quantity"][form="${form.id}"]`);
      if (linked) return linked;
    }

    return form.querySelector('[name="quantity"]') || scope.querySelector('.product-form__quantity .quantity__input');
  }

  function getQuantity(form) {
    const quantityInput = getQuantityInput(form);
    return Number.parseInt(quantityInput && quantityInput.value, 10) || 1;
  }

  function findVariant(form, variantId) {
    const fromJson = parsePayload(form).variants.find((variant) => String(variant.id) === String(variantId));
    if (fromJson) return fromJson;

    const card = document.querySelector(`.bundle-variant-card[data-variant-id="${variantId}"]`);
    if (!card) return null;

    return {
      id: variantId,
      quantity: Number.parseInt(card.dataset.inventoryQuantity, 10),
      policy: card.dataset.inventoryPolicy,
      management: card.dataset.inventoryManagement || null,
      title: card.dataset.variantTitle || '',
      units: Number.parseInt(card.dataset.bottleUnits, 10) || parseBottleUnits(card.dataset.variantTitle),
    };
  }

  function loadCart() {
    if (cartPromise) return cartPromise;

    cartPromise = fetch('/cart.js')
      .then((response) => response.json())
      .then((cart) => {
        cartData = cart;
        return cart;
      })
      .catch(() => {
        cartData = null;
        return null;
      })
      .finally(() => {
        cartPromise = null;
      });

    return cartPromise;
  }

  function cartBottlesForProduct(payload) {
    const productId = payload && payload.productId;
    if (!productId || !cartData || !Array.isArray(cartData.items)) {
      return Number(payload && payload.cartBottles) || 0;
    }

    return cartData.items.reduce((total, item) => {
      if (String(item.product_id) !== String(productId)) return total;

      const variant = (payload.variants || []).find((entry) => String(entry.id) === String(item.variant_id));
      const units = variant ? variantUnits(variant) : parseBottleUnits(item.variant_title);
      return total + units * (Number(item.quantity) || 0);
    }, 0);
  }

  function fillTemplate(template, values) {
    if (!template) return '';

    return template
      .replace(/__NOW__/g, String(values.now))
      .replace(/__LATER__/g, String(values.later))
      .replace(/__STOCK__/g, String(values.stock));
  }

  function getPool(form, variant, quantity) {
    const payload = parsePayload(form);
    const stock = payload.variants.length
      ? stockBottlesFromVariants(payload.variants)
      : Number(payload.stockBottles) || 0;
    const inCart = cartBottlesForProduct(payload);
    const requested = Math.max(1, Number(quantity) || 1) * variantUnits(variant);
    const remaining = Math.max(0, stock - inCart);
    const now = Math.min(requested, remaining);
    const later = Math.max(0, requested - now);

    return {
      stock,
      inCart,
      requested,
      remaining,
      now,
      later,
    };
  }

  function updateStockCopy(note, split) {
    const copyEl = note.querySelector('[data-preorder-copy]');
    if (!copyEl) return;

    const count = Math.max(0, Number(split.remaining) || 0);
    const locale = (document.documentElement.lang || 'en').toLowerCase();
    const useOne = locale.startsWith('fr') ? count < 2 : count === 1;
    const template = useOne ? note.dataset.noteOne : note.dataset.noteOther;
    if (!template) return;

    copyEl.innerHTML = template.replace(/__COUNT__/g, String(count));
  }

  function isPreorder(variant, pool) {
    if (!variant || variant.policy !== 'continue') return false;
    return pool.remaining < pool.requested;
  }

  function syncForm(form) {
    if (!form) return;

    const variantIdInput = form.querySelector('[name="id"]');
    if (!variantIdInput || variantIdInput.disabled) return;
    if (!form.querySelector(VARIANT_SELECTOR)) return;

    const variant = findVariant(form, variantIdInput.value);
    const quantity = getQuantity(form);
    const pool = getPool(form, variant, quantity);
    const preorder = isPreorder(variant, pool);

    const propertyInput = form.querySelector(PROPERTY_SELECTOR);
    const flagInput = form.querySelector(FLAG_SELECTOR);
    const productForm = form.closest('product-form');
    const note = productForm && productForm.querySelector(NOTE_SELECTOR);
    const addText = form.querySelector(ADD_TEXT_SELECTOR);
    const buyNow = form.querySelector(BUY_NOW_SELECTOR);

    if (propertyInput) {
      propertyInput.disabled = !preorder;
      propertyInput.value = preorder
        ? fillTemplate(note && note.dataset.propertyTemplate, pool) ||
          propertyInput.dataset.preorderValue ||
          propertyInput.value
        : '';
    }

    if (flagInput) {
      flagInput.disabled = !preorder;
      flagInput.value = preorder ? 'true' : '';
    }

    if (note) {
      if (preorder) updateStockCopy(note, pool);
      else closePopup(note.closest('[data-preorder-popup]'));
    }

    if (addText && addText.dataset.soldOut !== 'true') {
      addText.textContent = preorder ? addText.dataset.preorderLabel : addText.dataset.addLabel;
    }

    if (buyNow && buyNow.dataset.soldOut !== 'true') {
      buyNow.textContent = preorder ? buyNow.dataset.preorderLabel : buyNow.dataset.addLabel;
    }
  }

  function syncAllForms() {
    document.querySelectorAll('form[data-type="add-to-cart-form"]').forEach(syncForm);
  }

  function closestProductForm(target) {
    return target.closest('form[data-type="add-to-cart-form"]');
  }

  function formFromQuantityInput(input) {
    if (input.form) return input.form;
    if (input.hasAttribute('form')) return document.getElementById(input.getAttribute('form'));
    return closestProductForm(input);
  }

  function refreshFromCart() {
    return loadCart().then(syncAllForms);
  }

  const POPUP_SELECTOR = '[data-preorder-popup]';
  let bypassPopup = false;

  function closePopup(popup) {
    if (!popup) return;
    popup.hidden = true;
    popup._pendingButton = null;
    if (!document.querySelector(`${POPUP_SELECTOR}:not([hidden])`)) {
      document.body.classList.remove('preorder-popup-open');
    }
  }

  function openPopup(popup, button) {
    if (!popup) return;
    popup._pendingButton = button || null;
    popup.hidden = false;
    document.body.classList.add('preorder-popup-open');
    const confirmButton = popup.querySelector('[data-preorder-popup-confirm]');
    if (confirmButton) confirmButton.focus();
  }

  function preorderState(form) {
    const variantIdInput = form.querySelector('[name="id"]');
    if (!variantIdInput || !form.querySelector(VARIANT_SELECTOR)) return null;

    const productForm = form.closest('product-form');
    const popup = productForm && productForm.querySelector(POPUP_SELECTOR);
    const note = popup && popup.querySelector(NOTE_SELECTOR);
    if (!popup || !note) return null;

    const variant = findVariant(form, variantIdInput.value);
    const pool = getPool(form, variant, getQuantity(form));
    return { popup, note, pool, preorder: isPreorder(variant, pool) };
  }

  function init() {
    syncAllForms();
    refreshFromCart();

    document.addEventListener(
      'click',
      (event) => {
        if (bypassPopup) return;

        const confirmButton = event.target.closest('[data-preorder-popup-confirm]');
        if (confirmButton) {
          event.preventDefault();
          const popup = confirmButton.closest(POPUP_SELECTOR);
          const pendingButton = popup && popup._pendingButton;
          closePopup(popup);
          if (!pendingButton) return;
          bypassPopup = true;
          pendingButton.click();
          bypassPopup = false;
          return;
        }

        if (event.target.closest('[data-preorder-popup-close]')) {
          event.preventDefault();
          closePopup(event.target.closest(POPUP_SELECTOR));
          return;
        }

        const button = event.target.closest('.product-form__submit, [data-preorder-buy-now]');
        if (!button || button.disabled) return;

        const form = button.closest('form[data-type="add-to-cart-form"]');
        if (!form) return;

        const state = preorderState(form);
        if (!state || !state.preorder) return;

        event.preventDefault();
        event.stopPropagation();
        updateStockCopy(state.note, state.pool);
        openPopup(state.popup, button);
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (bypassPopup) return;
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;

        const state = preorderState(form);
        if (!state || !state.preorder) return;

        event.preventDefault();
        event.stopPropagation();
        updateStockCopy(state.note, state.pool);
        openPopup(state.popup, form.querySelector('.product-form__submit'));
      },
      true
    );

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      closePopup(document.querySelector(`${POPUP_SELECTOR}:not([hidden])`));
    });

    document.addEventListener('change', (event) => {
      if (!event.target.matches('[name="id"], [name="quantity"], .quantity__input')) return;
      syncForm(formFromQuantityInput(event.target) || closestProductForm(event.target));
    });

    document.addEventListener('input', (event) => {
      if (!event.target.matches('[name="quantity"], .quantity__input')) return;
      syncForm(formFromQuantityInput(event.target));
    });

    document.addEventListener('mixo:preorder-sync', (event) => {
      syncForm(event.detail && event.detail.form ? event.detail.form : closestProductForm(event.target));
    });

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, syncAllForms);
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshFromCart);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
